import React, { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvCompactButton from '../atoms/OvCompactButton';
import OvTextField from '../atoms/OvTextField';
import OvVideoThumbnail from '../atoms/OvVideoThumbnail';
import OvStagedPlacementSummary from './OvStagedPlacementSummary';
import OvVideoOrientationField from './OvVideoOrientationField';
import OvVideoStateNotice from './OvVideoStateNotice';
import {
  StagedVideo,
  VideoOrientation,
} from '../../../common/model/type/video-file.types';
import { formatMegabytes } from '../../../common/utils/video-file-utils';
import { isPlacementPositionValid } from '../../../common/utils/video-placement-utils';
import {
  isStagedPlaced,
  needsOrientation,
  stagedFieldId,
  stagedFieldsId,
} from '../../../common/utils/video-staging-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import breakpoints from '../../../design-system/breakpoints';

interface OvVideoStagingRowProps {
  item: StagedVideo;
  isDisabled: boolean;
  onTitleChange: (key: string, title: string) => void;
  onDescriptionChange: (key: string, description: string) => void;
  onOrientationChange: (key: string, orientation: VideoOrientation) => void;
  onRemove: (key: string) => void;
  variant?: StagingRowVariant;
  children?: ReactNode;
}

type StagingRowVariant = 'staging' | 'replacement';

const OvVideoStagingRow: FC<OvVideoStagingRowProps> = ({
  item,
  isDisabled,
  onTitleChange,
  onDescriptionChange,
  onOrientationChange,
  onRemove,
  variant = 'staging',
  children,
}) => {
  const { t } = useTranslation();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isPlacementOpen, setIsPlacementOpen] = useState(false);
  const { key, file, title, description, dimensions, orientation, thumbnail } =
    item;
  const groupId = stagedFieldsId(key);
  const isTitleMissing = !title.trim();
  const isPlacementShown =
    isPlacementOpen || !isPlacementPositionValid(item.placement);
  const isPlaced = isStagedPlaced(item);
  const hasPlacement = variant === 'staging';
  const isDetailsShown = isDetailsOpen || !hasPlacement;

  const getThumbnailLabel = () =>
    thumbnail === 'reading'
      ? t('videos.staging.loadingPreview')
      : t('videos.staging.previewUnavailable');

  const renderDimensions = () => {
    if (dimensions === 'reading') {
      return <Meta>{t('videos.staging.readingSize')}</Meta>;
    }

    if (dimensions) {
      return (
        <Meta>
          {t('videos.staging.size', {
            width: dimensions.width,
            height: dimensions.height,
          })}
        </Meta>
      );
    }

    return null;
  };

  return (
    <Row>
      <Main>
        <OvVideoThumbnail
          src={thumbnail === 'reading' ? null : thumbnail}
          placeholderLabel={getThumbnailLabel()}
        />
        <Summary>
          <OvTextField
            id={stagedFieldId(key, 'title')}
            size="small"
            label={t('videos.fields.title')}
            value={title}
            disabled={isDisabled}
            required
            error={isTitleMissing}
            helperText={
              isTitleMissing ? t('videos.staging.titleRequired') : undefined
            }
            onChange={(event) => onTitleChange(key, event.target.value)}
          />
          <Meta>
            {file.name} ·{' '}
            {t('videos.staging.megabytes', {
              size: formatMegabytes(file.size),
            })}
          </Meta>
          {hasPlacement && (
            <Meta>
              <OvStagedPlacementSummary values={item.placement} />
            </Meta>
          )}
        </Summary>
        <OvCompactButton
          tone="secondary"
          aria-label={t('videos.staging.removeFile', { name: file.name })}
          disabled={isDisabled}
          onClick={() => onRemove(key)}
        >
          {t('videos.staging.remove')}
        </OvCompactButton>
      </Main>

      {needsOrientation(item) && (
        <OvVideoOrientationField
          itemKey={key}
          orientation={orientation}
          isDisabled={isDisabled}
          onChange={onOrientationChange}
        />
      )}
      {dimensions === 'reading' && renderDimensions()}

      {hasPlacement && (
        <Toggles>
          <OvCompactButton
            tone="secondary"
            aria-expanded={isDetailsOpen}
            aria-controls={`${groupId}-details`}
            onClick={() => setIsDetailsOpen((open) => !open)}
          >
            {t('videos.staging.moreDetails')}
          </OvCompactButton>
          <OvCompactButton
            tone="secondary"
            aria-expanded={isPlacementShown}
            aria-controls={`${groupId}-where`}
            onClick={() => setIsPlacementOpen(!isPlacementShown)}
          >
            {t(
              isPlaced
                ? 'videos.staging.editPlacement'
                : 'videos.staging.setPlacement'
            )}
          </OvCompactButton>
        </Toggles>
      )}

      {isDetailsShown && (
        <Group id={`${groupId}-details`}>
          <OvTextField
            size="small"
            label={t('videos.fields.description')}
            value={description}
            disabled={isDisabled}
            multiline
            onChange={(event) => onDescriptionChange(key, event.target.value)}
          />
          {dimensions !== 'reading' && renderDimensions()}
        </Group>
      )}

      {hasPlacement && isPlacementShown && (
        <Group id={`${groupId}-where`}>
          {children}
          {isPlaced && (
            <OvVideoStateNotice
              notice={{ kind: 'stagedAppears' }}
              variant="compact"
            />
          )}
        </Group>
      )}
    </Row>
  );
};

export default OvVideoStagingRow;

const Row = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: ${Variables.spacing.SMALL} 0;
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const Main = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Variables.spacing.SMALL};

  @media (max-width: ${breakpoints.md}) {
    flex-wrap: wrap;
  }
`;

const Summary = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 12rem;
`;

const Meta = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
  overflow-wrap: anywhere;
`;

const Toggles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.SMALL};
  padding-left: ${Variables.spacing.MEDIUM};
  border-left: 2px solid ${Colours.OV_BORDER_COLOR_V3};
`;
